import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { User, UserFormData, formatErrorMessage } from '../types';
import { useStrictModeGuard } from '../hooks/useStrictModeGuard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';

const MyUsers: React.FC = () => {
  const { user } = useAuth();
  const { executeOnce } = useStrictModeGuard();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    username: '',
    password: '',
    role: 'organization_user'
  });

  const fetchMyUsers = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await axios.get('/users/my-organization', {
        params: { skip, limit: itemsPerPage }
      });
      const responseData = response.data;
      const usersData = responseData.items || responseData; // Handle both old and new format
      
      setUsers(usersData);
      
      // Update pagination info from response
      if (responseData.total_count !== undefined) {
        setTotalItems(responseData.total_count);
        setTotalPages(responseData.total_pages);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    executeOnce(() => {
      fetchMyUsers();
    });
  }, [fetchMyUsers, executeOnce]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingUser) {
        // Don't send password when editing
        delete submitData.password;
        await axios.put<User>(`/users/my-organization/${editingUser.id}`, submitData);
      } else {
        await axios.post<User>('/users/my-organization', submitData);
      }

      await fetchMyUsers();
      resetForm();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`/users/my-organization/${id}`);
      await fetchMyUsers();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handleEdit = (userItem: User): void => {
    setEditingUser(userItem);
    setFormData({
      email: userItem.email,
      username: userItem.username,
      password: '', // Password is not pre-filled for security
      role: userItem.role
    });
    setShowCreateForm(true);
  };

  const resetForm = (): void => {
    setFormData({ email: '', username: '', password: '', role: 'organization_user' });
    setShowCreateForm(false);
    setEditingUser(null);
    setError(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div>Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        backgroundColor: '#fee2e2', 
        color: '#dc2626', 
        padding: '12px', 
        borderRadius: '6px', 
        marginBottom: '16px' 
      }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, color: '#1f2937' }}>My Users</h1>
      </div>

      {/* Add/Edit User Form */}
      {showCreateForm && (
        <div className="card mb-6">
          <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            {!editingUser && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="organization_user">Organization User</option>
                <option value="organization_admin">Organization Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#16a34a',
                  border: '2px solid white',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  minWidth: '140px',
                  minHeight: '40px'
                }}
              >
                <div style={{
                  backgroundColor: '#16a34a',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <FontAwesomeIcon icon={editingUser ? faSave : faPlus} style={{ fontSize: '14px' }} />
                </div>
                <div style={{
                  padding: '10px 12px',
                  flex: 1,
                  textAlign: 'center'
                }}>
                  {editingUser ? 'UPDATE USER' : 'ADD USER'}
                </div>
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#6b7280',
                  border: '2px solid white',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  minWidth: '100px',
                  minHeight: '40px'
                }}
              >
                <div style={{
                  padding: '10px 12px',
                  flex: 1,
                  textAlign: 'center'
                }}>
                  CANCEL
                </div>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add User Button */}
      {!showCreateForm && (
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#2563eb',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              padding: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              cursor: 'pointer',
              overflow: 'hidden',
              minWidth: '160px',
              minHeight: '40px'
            }}
          >
            <div style={{
              backgroundColor: '#2563eb',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)'
            }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '14px' }} />
            </div>
            <div style={{
              padding: '10px 12px',
              flex: 1,
              textAlign: 'center'
            }}>
              ADD NEW USER
            </div>
          </button>
        </div>
      )}

      {/* Users List */}
      {users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          No users found in your organization.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Username</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Joined</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userItem) => (
                <tr key={userItem.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px' }}>{userItem.username}</td>
                  <td style={{ padding: '12px' }}>{userItem.email}</td>
                  <td style={{ padding: '12px' }}>
                    {userItem.role === 'super_admin' ? (
                      <span style={{ 
                        backgroundColor: '#fef3c7', 
                        color: '#d97706', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px' 
                      }}>
                        Super Admin
                      </span>
                    ) : userItem.role === 'organization_admin' ? (
                      <span style={{ 
                        backgroundColor: '#dbeafe', 
                        color: '#1d4ed8', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px' 
                      }}>
                        Org Admin
                      </span>
                    ) : (
                      <span style={{ 
                        backgroundColor: '#f3f4f6', 
                        color: '#374151', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px' 
                      }}>
                        User
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ 
                      backgroundColor: userItem.is_active ? '#dcfce7' : '#fee2e2', 
                      color: userItem.is_active ? '#166534' : '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '12px' 
                    }}>
                      {userItem.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {new Date(userItem.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(userItem)}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#f59e0b',
                          border: '2px solid white',
                          borderRadius: '6px',
                          color: 'white',
                          padding: '0',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          minWidth: '60px',
                          minHeight: '28px'
                        }}
                      >
                        <div style={{
                          backgroundColor: '#f59e0b',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: '1px solid rgba(255,255,255,0.3)'
                        }}>
                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '10px' }} />
                        </div>
                        <div style={{
                          padding: '4px 6px',
                          flex: 1,
                          textAlign: 'center'
                        }}>
                          EDIT
                        </div>
                      </button>
                      {userItem.role !== 'super_admin' && userItem.id !== user?.id && (
                        <button
                          onClick={() => handleDelete(userItem.id)}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#dc2626',
                            border: '2px solid white',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '0',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            minWidth: '70px',
                            minHeight: '28px'
                          }}
                        >
                          <div style={{
                            backgroundColor: '#dc2626',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: '1px solid rgba(255,255,255,0.3)'
                          }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                          </div>
                          <div style={{
                            padding: '4px 6px',
                            flex: 1,
                            textAlign: 'center'
                          }}>
                            DELETE
                          </div>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {users.length > 0 && (
        <div className="card">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showInfo={true}
          />
        </div>
      )}
    </div>
  );
};

export default MyUsers;
